import { isWeb, useMaxWidth } from '@/components/WebWrapper'
import { useResponsive } from '@/screens/new-design/hooks'
import { isNumber } from 'lodash'
import { useWindowDimensions, ViewStyle } from 'react-native'

// Constrain width based on available height to avoid scrolling
// Using a factor of 0.55 to approximate the game board aspect ratio
const TABLE_ASPECT_RATIO = 0.55

// Design aspect ratios
// Card: window width = 402 / card width = 38  >> 10.5
// Seat: window width = 402 / seat width = 96  >> 4.1875
// Player Hand Card: window width = 402 / card width = 48  >> 10.5
// Table: window width = 402 / card width = 146 >> 2.75
const BoardLayoutConstants = {
  getCardWidth: (w: number) => {
    return Math.round(w / 10.5)
  },
  getHandCardWidth: (w: number) => {
    return Math.round(w / 8.375)
  },
  getInnerTableWidth: (w: number) => {
    return Math.round(w / 2.75)
  },
  getTableWidth: (w: number) => {
    return Math.round(Math.min(w * 0.8, 400))
  },
  getSeatWidth: (w: number) => {
    return Math.round(w / 4.1875)
  },
  getBoardCardHeight: (w: number) => {
    return Math.round(w * 0.25)
  }
}

// TODO: verify if isWeb needs cardWith of 38
const useTableLayout = () => {
  const windowSize = useWindowDimensions()

  const _maxWidth = useMaxWidth()
  const maxWidth = isNumber(_maxWidth) ? _maxWidth : 0

  let width = maxWidth > 0 ? Math.min(maxWidth, windowSize.width) : windowSize.width

  if (isWeb) {
    width = Math.min(width, windowSize.height * TABLE_ASPECT_RATIO)
  }

  const tableWidth = BoardLayoutConstants.getTableWidth(width)
  const tableHeight = Math.round(tableWidth * 1.6)

  const cardWidth = Math.round(BoardLayoutConstants.getCardWidth(width))
  const cardHeight = Math.round(cardWidth * 1.47)

  const handCardWidth = Math.round(BoardLayoutConstants.getHandCardWidth(width))
  const innerTableWidth = Math.round(BoardLayoutConstants.getInnerTableWidth(width))
  const seatsWidth = Math.round(BoardLayoutConstants.getSeatWidth(width))

  const padding = 8 as const

  return {
    windowSize,
    maxWidth,
    padding,
    header: {
      height: 54,
      icon: 24
    },
    communityCard: {
      width: cardWidth,
      height: cardHeight
    },
    chips: {
      size: 14
    },
    handCard: {
      width: handCardWidth
    },
    innerTable: {
      width: innerTableWidth,
      height: Math.round(innerTableWidth * 1.6)
    },
    table: {
      width: tableWidth,
      height: tableHeight
    },
    seats: {
      width: seatsWidth,
      // height: seatsWidth,
      height: Math.round(seatsWidth * 1.04),
      borderRadius: padding * 0.5
    },
    boardSpaceForCard: {
      // height: 120
      height: cardHeight * 1.5 // cardHeight + innerPadding
    },
    bottomControls: {
      height: 150 // TODO: REVIEW: calculate base on other number
    }
  }
}

export const useTableLayoutPosition = () => {
  const layout = useTableLayout()
  const horizontalGap = 0
  const { seatsLayout, seatTopSpacer, seatsTotalHeight } = useSeatsPositions(layout.seats.width, horizontalGap)

  return {
    ...layout,
    seatsLayout: {
      positions: seatsLayout,
      topSpacer: seatTopSpacer,
      height: seatsTotalHeight
    }
  }
}

const HAS_TOP_SEAT = true

const useSeatsPositions = (seatWidth = 120, horizontalGap = 0) => {
  const layout = useTableLayout()

  const seatSpacer = layout.padding * 2
  const seatSpace = layout.seats.height + seatSpacer

  const seatTopSpacer = HAS_TOP_SEAT ? layout.seats.height - layout.header.height : 0

  const seatColumnLayout_one = seatSpacer
  const seatColumnLayout_two = seatColumnLayout_one + seatSpace

  const afterCommunityCardOffset = layout.boardSpaceForCard.height

  const seatColumnLayout_tree = seatColumnLayout_two + seatSpace + afterCommunityCardOffset
  const seatColumnLayout_four = seatColumnLayout_tree + seatSpace

  const seatColumnLayout = {
    1: {
      top: seatColumnLayout_one
    },
    2: {
      top: seatColumnLayout_two
    },
    3: {
      top: seatColumnLayout_tree
    },
    4: {
      top: seatColumnLayout_four
    }
  } satisfies Record<number, { top: number }>

  type Seat = {
    seatId: number
    alignment: 'right' | 'left' | 'top' | 'bottom'
    style?: Pick<ViewStyle, 'top' | 'right' | 'left' | 'marginLeft' | 'transform'>
  }
  const seatsLayout: Seat[] = [
    {
      seatId: 0,
      alignment: 'left',
      style: {
        top: seatColumnLayout[1].top,
        left: horizontalGap
      }
    },
    {
      seatId: 1,
      alignment: 'left',
      style: {
        top: seatColumnLayout[2].top,
        left: horizontalGap
      }
    },
    {
      seatId: 2,
      alignment: 'left',
      style: {
        top: seatColumnLayout[3].top,
        left: horizontalGap
      }
    },
    {
      seatId: 3,
      alignment: 'left',
      style: {
        top: seatColumnLayout[4].top,
        left: horizontalGap
      }
    },
    // From player perspective, is sitting in this position
    {
      seatId: 4,
      alignment: 'bottom',
      style: {
        top: seatColumnLayout[4].top + layout.seats.height / 2,
        left: '50%',
        marginLeft: -seatWidth * 0.5
      }
    },
    {
      seatId: 5,
      alignment: 'right',
      style: {
        top: seatColumnLayout[4].top,
        right: horizontalGap
      }
    },
    {
      seatId: 6,
      alignment: 'right',
      style: {
        top: seatColumnLayout[3].top,
        right: horizontalGap
      }
    },
    {
      seatId: 7,
      alignment: 'right',
      style: {
        top: seatColumnLayout[2].top,
        right: horizontalGap
      }
    },
    {
      seatId: 8,
      alignment: 'right',
      style: {
        top: seatColumnLayout[1].top,
        right: horizontalGap
      }
    },
    // top
    {
      seatId: 9,
      alignment: 'top',
      style: {
        top: -layout.header.height,
        left: '50%',
        marginLeft: -seatWidth * 0.5
      }
    }
  ]

  return { seatsLayout, seatTopSpacer, seatsTotalHeight: seatColumnLayout_four + seatSpace }
}
